import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client';
import type { PlansResponse } from '../api/types';
import { Nettstedsbunn, Nettstedstopp, useHovedvei } from '../components/Nettsted';
import { PlusProveTilbud } from '../components/Proveperiode';
import { meldLanding } from '../lib/trakt';
import { formatNok } from '../lib/format';
import { SIDETITLER, useSidehode } from '../lib/sidehode';
import '../styles/landing.css';
import { Ikon, type IkonNavn } from '../components/icons';
import { prislinje } from '../lib/selskap';

const TOOL_CARDS: { icon: IkonNavn; title: string; plan: string; body: string }[] = [
  {
    icon: 'gjester',
    title: 'Gjesteliste og RSVP',
    plan: 'Gratis',
    body: 'Hver gjest får sin egen svarlenke. Svar, allergier og følge samles automatisk – ingen regneark, ingen purringer på Messenger.',
  },
  {
    icon: 'budsjett',
    title: 'Budsjett',
    plan: 'Gratis',
    body: 'Sett rammen én gang, og se hva som er planlagt, betalt og fortsatt utestående. Kategoriene er ferdig satt opp for norske bryllup.',
  },
  {
    icon: 'oppgaver',
    title: 'Oppgaver',
    plan: 'Gratis',
    body: 'Start med en ferdig sjekkliste tilpasset bryllupsdatoen, eller lag deres egen. Samle alle oppgavene på ett sted og få full kontroll over hva som skal gjøres, fristene og det som er ferdig.',
  },
  {
    icon: 'bordplassering',
    title: 'Bordplassering',
    plan: 'Plus',
    body: 'Finn en god plass til alle. Dra gjestene rundt i bordkartet og prøv ulike plasseringer til dere er fornøyde.',
  },
];

const STEPS = [
  {
    title: 'Fortell om dagen',
    body: 'Fire korte spørsmål: navn, dato, sted og omtrent hvor mange dere blir. Det tar under ett minutt.',
  },
  {
    title: 'Legg inn gjestene',
    body: 'Lim inn listen om dere allerede har den, eller skriv dem inn. Alle får hver sin svarlenke å sende ut.',
  },
  {
    title: 'Følg med mens svarene kommer',
    body: 'Oversikten oppdaterer seg selv. Når svarene er inne, setter dere bordplasseringen på plass.',
  },
];

export function LandingPage() {
  const [plans, setPlans] = useState<PlansResponse | null>(null);
  const { href: primaryHref, tekst: primaryLabel, innlogget: loggedIn } = useHovedvei();

  // Forsiden setter tittelen sin selv, selv om serveren allerede har gjort det:
  // kommer man hit fra prislista, er det uten at nettleseren laster på nytt, og
  // da er det prislistas tittel som ellers ville blitt stående i fanen.
  useSidehode(SIDETITLER.forside, '/');

  useEffect(() => {
    meldLanding('forside');
  }, []);

  useEffect(() => {
    api
      .get<PlansResponse>('/billing/plans')
      .then(setPlans)
      .catch(() => setPlans(null));
  }, []);

  return (
    <>
      <Nettstedstopp
        snarveier={[
          { href: '#slik', tekst: 'Slik virker det' },
          { href: '#verktoy', tekst: 'Verktøy' },
        ]}
      />

      <main>
        <section className="hero">
          <div className="container inner">
            <div>
              <span className="eyebrow">Bryllupsplanlegging</span>
              <h1>
                Alt om <em>deres dag</em>, samlet ett sted
              </h1>
              <p className="hero-lead">
                Få oversikt over gjestene, budsjettet og alt som skal på plass før den store
                dagen. Planlegg sammen, og få mer tid til å glede dere. Start gratis med
                gjesteliste og svar på invitasjonene.
              </p>
              <div className="hero-cta">
                <Link className="btn btn-primary btn-lg" to={primaryHref}>
                  {primaryLabel}
                </Link>
                <a className="btn btn-secondary btn-lg" href="#slik">
                  Se hvordan det virker
                </a>
              </div>
              <p className="hero-note">
                Gratis å komme i gang. Du trenger ikke betalingskort.
              </p>
            </div>

            {/* Et lite utsnitt av bordkartet – viser produktet i stedet for å beskrive det. */}
            <div className="hero-art" aria-hidden="true">
              <div className="hero-art-bar">
                <i />
                <i />
                <i />
                <span>Bordplassering</span>
              </div>
              <div className="hero-floor">
                <div
                  className="hero-table"
                  style={{ width: 116, height: 116, left: 78, top: 66 }}
                >
                  Hovedbord
                </div>
                {[
                  [136, 48],
                  [188, 74],
                  [200, 128],
                  [160, 172],
                  [104, 180],
                  [58, 154],
                  [44, 96],
                  [82, 48],
                ].map(([x, y], index) => (
                  <div
                    key={`${x}-${y}`}
                    className={`hero-seat${index % 3 === 0 ? ' rose' : ''}`}
                    style={{ left: x, top: y }}
                  >
                    {['IB', 'MS', 'KN', 'ON', 'HL', 'AD', 'NS', 'EM'][index]}
                  </div>
                ))}
                <div className="hero-chip dragging" style={{ left: 226, top: 196 }}>
                  Ingrid Berg
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section section-alt" id="slik">
          <div className="container">
            <div className="section-head center">
              <h2>Tre steg, så er dere i gang</h2>
              <p>
                Svar på noen raske spørsmål om bryllupet så er dere i gang.
              </p>
            </div>
            <div className="steps">
              {STEPS.map((step) => (
                <article className="step" key={step.title}>
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section" id="verktoy">
          <div className="container">
            <div className="section-head">
              <h2>Fra første gjest til siste bordplass</h2>
              <p>
                Samle gjesteliste, budsjett og oppgaver på ett sted. Når svarene kommer inn,
                har dere gjestene klare til bordplasseringen.
              </p>
            </div>
            <div className="features">
              {TOOL_CARDS.map((tool) => (
                <article className="feature" key={tool.title}>
                  <div className="feature-icon">
                    <Ikon navn={tool.icon} />
                  </div>
                  <h3>
                    {tool.title}
                    <span
                      className={`badge ${
                        tool.plan === 'Plus'
                          ? 'badge-accent'
                          : tool.plan === 'Standard'
                            ? 'badge-primary'
                            : 'badge-success'
                      }`}
                    >
                      {tool.plan}
                    </span>
                  </h3>
                  <p>{tool.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section section-alt" id="priser">
          <div className="container">
            <div className="section-head center">
              <h2>Én pris for hele bryllupet</h2>
              <p>
                Betal én gang for hele bryllupet, uansett antall gjester. Dere kan bruke
                Vår Dag i 6 måneder etter bryllupet.
              </p>
            </div>

            {loggedIn ? null : <PlusProveTilbud />}

            {plans ? (
              <div className="pricing">
                {plans.plans.map((plan) => (
                  <article
                    className={`plan-card${plan.recommended ? ' recommended' : ''}`}
                    key={plan.id}
                  >
                    {plan.recommended ? <span className="plan-flag">Mest valgt</span> : null}
                    <h3 className="plan-name">{plan.name}</h3>
                    <p className="plan-tagline">{plan.tagline}</p>
                    <div className="plan-price">
                      {plan.priceNok === 0 ? 'Gratis' : formatNok(plan.priceNok)}
                    </div>
                    <div className="small muted">
                      {plan.priceNok === 0 ? 'For alltid' : prislinje()}
                    </div>
                    <ul className="plan-list">
                      {plan.highlights.map((highlight) => (
                        <li key={highlight}>{highlight}</li>
                      ))}
                      {plan.roadmap.map((item) => (
                        <li className="soon" key={item}>
                          {item} <span className="tiny">(kommer)</span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      className={`btn ${plan.recommended ? 'btn-primary' : 'btn-secondary'} btn-block`}
                      to={loggedIn ? '/app/abonnement' : '/registrer'}
                    >
                      {plan.priceNok === 0 ? 'Start gratis' : `Velg ${plan.name}`}
                    </Link>
                  </article>
                ))}
              </div>
            ) : (
              <p className="center muted">Henter priser …</p>
            )}

            {/* Bolken her er de tre tallene. Sammenligningen, angreretten og
                spørsmålene folk faktisk stiller, står på prissiden – den som
                kan deles og finnes i et søk. */}
            <p className="center" style={{ marginTop: '1.5rem' }}>
              <Link to="/priser">Se hva som ligger i hver pakke →</Link>
            </p>
            <p className="center small muted" style={{ marginTop: '0.5rem' }}>
              Betal med Vipps eller kort. Ett engangsbeløp for hele bryllupet, ikke et
              månedsabonnement.
            </p>
          </div>
        </section>

        <section className="section">
          <div className="container quote">
            <blockquote>
              «Vi hadde gjestelisten i ett regneark, budsjettet i et annet og bordkartet på et
              serviettark. Nå ligger alt sammen – og begge to ser det samme.»
            </blockquote>
            <p className="muted small">Slik skal planlegging føles. Det er hele poenget.</p>
          </div>
        </section>

        <section className="section" style={{ paddingTop: 0 }}>
          <div className="container">
            <div className="cta-band">
              <h2>Klare til å begynne?</h2>
              <p>
                Opprett bryllupet, legg inn de første gjestene og send ut svarlenkene. Det
                koster ingenting å prøve.
              </p>
              <Link className="btn btn-primary btn-lg" to={primaryHref}>
                {primaryLabel}
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Nettstedsbunn />
    </>
  );
}
